import { useCallback, useEffect, useRef, useState } from "react";

import { getGeoRegions } from "../api/geo";
import { geoMessageFromError } from "../lib/geoErrors";

// Loads active O/K regions once per mount. Optional module cache avoids
// duplicate requests when the user re-opens the location form in-session.

let cachedRegions = null;

export function useGeoRegions() {
  const [regions, setRegions] = useState(cachedRegions || []);
  const [loading, setLoading] = useState(!cachedRegions);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const load = useCallback((force = false) => {
    if (!force && cachedRegions) {
      setRegions(cachedRegions);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    return getGeoRegions()
      .then((data) => {
        if (!mountedRef.current) return;
        const list = Array.isArray(data) ? data : [];
        const sorted = [...list].sort((a, b) =>
          a.name.localeCompare(b.name, "uk"),
        );
        cachedRegions = sorted;
        setRegions(sorted);
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        setError(geoMessageFromError(err));
        setRegions([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  const reload = useCallback(() => {
    cachedRegions = null;
    return load(true);
  }, [load]);

  return { regions, loading, error, reload };
}

// Test helper — reset module cache between tests.
export function __resetGeoRegionsCacheForTests() {
  cachedRegions = null;
}
